import { executeAgentNode } from "@/services/workflowEngine/agentNodeExecutor";
import { createEmptyRunState } from "@/types/workflowEngine";
import type { WorkflowNode } from "@/types/aiToolkit";
import { AGENTS } from "@/data/aiToolkit";
import { tagsService } from "@/services/tagsService";

/** localStorage keys for last-used agents (UI preference, deliberately not profile data). */
export const ACTION_PLAN_LAST_AGENT_KEY = "thread_action_plan_last_agent_id";
export const SUMMARIZE_LAST_AGENT_KEY = "thread_summarize_last_agent_id";

export function getLastUsedAgentId(storageKey: string): string | null {
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

export function setLastUsedAgentId(storageKey: string, agentId: string): void {
  try {
    localStorage.setItem(storageKey, agentId);
  } catch {
    // storage unavailable — non-fatal, selection just won't stick
  }
}

/** Last-used agent, else a built-in whose name matches the hint, else the first built-in. */
export function getDefaultAgentId(storageKey: string, nameHint: RegExp): string | null {
  const last = getLastUsedAgentId(storageKey);
  if (last) return last;
  const byName = AGENTS.find((a) => nameHint.test(a.name));
  return byName?.id ?? AGENTS[0]?.id ?? null;
}

export interface ThreadActionAgentResult {
  raw: string;
  parsed?: unknown;
}

/**
 * Run an agent against thread content. Uses the same executor as workflow agent
 * nodes (credits gate, skills, worker profile, ai-proxy routing).
 */
export async function runThreadActionAgent(input: {
  agentId: string;
  instruction: string;
  content: string;
  label?: string;
}): Promise<ThreadActionAgentResult> {
  const state = createEmptyRunState({ body: input.content });
  const node: WorkflowNode = {
    id: "thread-action",
    type: "agent",
    position: { x: 0, y: 0 },
    data: {
      label: input.label ?? "Thread action",
      agentId: input.agentId,
    },
  };

  const userContent = `${input.instruction}\n\n---\n\n${input.content}`;
  const result = await executeAgentNode(node, state, { userContent });

  const err = result.parsed as { error?: string } | undefined;
  if (err?.error) {
    throw new Error(err.error);
  }
  if (!result.raw.trim()) {
    throw new Error("The agent returned an empty result");
  }
  return { raw: result.raw.trim(), parsed: result.parsed };
}

/** Title from the first markdown heading, else the first non-empty line, else the fallback. */
export function deriveTitleFromMarkdown(markdown: string, fallback: string): string {
  const lines = markdown
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const heading = lines.find((l) => /^#{1,6}\s+/.test(l));
  const candidate = (heading ?? lines[0] ?? "")
    .replace(/^#{1,6}\s+/, "")
    .replace(/[*_`]/g, "")
    .trim();
  const title = candidate || fallback;
  return title.length > 120 ? `${title.slice(0, 117)}...` : title;
}

export interface SummaryAgentResult {
  title: string;
  content: string;
  tags: string[];
}

/**
 * Interpret a summarize-agent response. Prefers the requested JSON shape
 * ({ title, content, tags }) but tolerates plain-markdown replies.
 */
export function parseSummaryAgentResult(
  raw: string,
  parsed: unknown,
  fallbackTitle: string
): SummaryAgentResult {
  const obj = (
    parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null
  ) as Record<string, unknown> | null;

  let title = typeof obj?.title === "string" && obj.title.trim() ? obj.title.trim() : "";
  let content =
    typeof obj?.content === "string" && obj.content.trim() ? obj.content.trim() : "";
  const tags = Array.isArray(obj?.tags)
    ? obj.tags
        .filter((t): t is string => typeof t === "string" && !!t.trim())
        .map((t) => t.trim())
        .slice(0, 8)
    : [];

  if (!content) {
    content = raw.replace(/```json\s*[\s\S]*?```/g, "").trim() || raw;
  }
  if (!title) {
    title = deriveTitleFromMarkdown(content, fallbackTitle);
  }
  if (!/^MD:\s*/i.test(title)) {
    title = `MD: ${title}`;
  }
  return { title, content, tags };
}

export const ACTION_PLAN_INSTRUCTION = `Convert the AI response below into a clear, actionable plan in Markdown.
- Start with a single "# " heading that names the plan (it becomes the document title).
- Group tasks under "## " phase or category headings when it helps.
- Use "- [ ] " checkbox items for every task; make each task concrete and self-contained.
- Preserve any owners, dates, or amounts mentioned.
- Return ONLY the markdown plan — no commentary before or after.`;

export const SUMMARIZE_INSTRUCTION = `Create a markdown summary of the content below.
Return ONLY a JSON object with exactly these keys:
{
  "title": "a short document title that starts with 'MD: '",
  "content": "the full summary as well-structured Markdown",
  "tags": ["3 to 6 short topical tags"]
}
When the content references named documents or sources, cite them by title inline in "content" (e.g., (Source: Project Plan)).
Return the JSON object only — no code fences, no commentary.`;

/**
 * Flatten thread messages for summarization, newest-last, capped at maxChars
 * (keeps the most recent messages; S7 data minimization).
 */
export function formatThreadMessagesForSummary(
  messages: ReadonlyArray<{ role: string; content: unknown } | null | undefined>,
  maxChars = 60_000
): string {
  const parts: string[] = [];
  let total = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    const text = typeof msg?.content === "string" ? msg.content.trim() : "";
    if (!text) continue;
    const label = msg?.role === "user" ? "User" : "Assistant";
    const block = `${label}:\n${text}`;
    total += block.length + 2;
    parts.push(block);
    if (total > maxChars) break;
  }
  return parts.reverse().join("\n\n");
}

/** Resolve tag names to ids, creating missing tags. Failures skip the tag, never throw. */
export async function ensureTagIds(tagNames: string[]): Promise<string[]> {
  const ids: string[] = [];
  for (const name of tagNames) {
    const trimmed = name.trim();
    if (!trimmed) continue;
    try {
      let tag = await tagsService.getTagByName(trimmed);
      if (!tag) {
        tag = await tagsService.createTag({ name: trimmed });
      }
      if (tag?.id && !ids.includes(tag.id)) ids.push(tag.id);
    } catch (err) {
      console.warn("Tag resolution failed for", trimmed, err);
    }
  }
  return ids;
}
